/**
 * Theme Toggle Component - Premium Liquid Crystal Design
 * ========================================================
 * Animated sun/moon toggle for dark mode switching.
 * Apple-inspired smooth animations.
 */

import React from 'react';
import { Sun, Moon } from 'lucide-react';
import { useDarkMode } from '@/contexts/DarkModeContext';

export const ThemeToggle = ({ className = '', size = 'default' }) => {
  const { isDark, toggle } = useDarkMode();
  
  const sizeClasses = {
    sm: 'w-8 h-8',
    default: 'w-10 h-10',
    lg: 'w-12 h-12',
  };
  
  const iconSizes = {
    sm: 'w-4 h-4',
    default: 'w-5 h-5',
    lg: 'w-6 h-6',
  };

  return (
    <button
      onClick={toggle}
      data-testid="theme-toggle"
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      className={`
        relative overflow-hidden
        ${sizeClasses[size]}
        rounded-2xl
        bg-gradient-to-br ${isDark 
          ? 'from-indigo-500/20 to-violet-500/10 hover:from-indigo-500/30 hover:to-violet-500/20' 
          : 'from-amber-500/20 to-orange-500/10 hover:from-amber-500/30 hover:to-orange-500/20'
        }
        border border-white/20 dark:border-white/10
        backdrop-blur-xl
        shadow-[0_4px_16px_rgb(0,0,0,0.06),inset_0_0_0_1px_rgba(255,255,255,0.3)]
        dark:shadow-[0_4px_16px_rgb(0,0,0,0.2),inset_0_0_0_1px_rgba(255,255,255,0.1)]
        transition-all duration-500 ease-out
        hover:scale-105 hover:shadow-lg
        active:scale-95
        flex items-center justify-center
        ${className}
      `}
    >
      {/* Background glow effect */}
      <div className={`
        absolute inset-0 rounded-2xl transition-opacity duration-500
        ${isDark 
          ? 'bg-gradient-to-br from-indigo-400/30 to-violet-400/20 opacity-0 group-hover:opacity-100' 
          : 'bg-gradient-to-br from-amber-400/30 to-orange-400/20 opacity-0 group-hover:opacity-100'
        }
      `} />
      
      {/* Sun Icon */}
      <Sun 
        className={`
          ${iconSizes[size]}
          absolute transition-all duration-500 ease-out
          ${isDark 
            ? 'opacity-0 scale-50 rotate-180 text-amber-400' 
            : 'opacity-100 scale-100 rotate-0 text-amber-500'
          }
        `}
      />
      
      {/* Moon Icon */}
      <Moon 
        className={`
          ${iconSizes[size]}
          absolute transition-all duration-500 ease-out
          ${isDark 
            ? 'opacity-100 scale-100 rotate-0 text-indigo-400' 
            : 'opacity-0 scale-50 -rotate-180 text-indigo-500'
          }
        `}
      />
    </button>
  );
};

/**
 * Compact Theme Toggle - for tight spaces
 */
export const ThemeToggleCompact = ({ className = '' }) => {
  const { isDark, toggle } = useDarkMode();

  return (
    <button
      onClick={toggle}
      data-testid="theme-toggle-compact"
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      className={`
        relative
        w-14 h-7
        rounded-full
        bg-gradient-to-r ${isDark 
          ? 'from-indigo-600 to-violet-600' 
          : 'from-amber-400 to-orange-400'
        }
        p-1
        shadow-[0_2px_8px_rgb(0,0,0,0.1),inset_0_1px_1px_rgba(255,255,255,0.3)]
        transition-all duration-500
        ${className}
      `}
    >
      {/* Track background */}
      <div className={`
        absolute inset-0 rounded-full transition-opacity duration-500
        ${isDark 
          ? 'bg-gradient-to-r from-slate-800/50 to-slate-900/50' 
          : 'bg-gradient-to-r from-sky-100/50 to-blue-100/50'
        }
      `} />
      
      {/* Toggle knob */}
      <div className={`
        relative w-5 h-5
        rounded-full
        bg-white
        shadow-md
        flex items-center justify-center
        transform transition-transform duration-500 ease-out
        ${isDark ? 'translate-x-7' : 'translate-x-0'}
      `}>
        <Sun className={`w-3 h-3 text-amber-500 absolute transition-all duration-500 ${isDark ? 'opacity-0 scale-50' : 'opacity-100 scale-100'}`} />
        <Moon className={`w-3 h-3 text-indigo-500 absolute transition-all duration-500 ${isDark ? 'opacity-100 scale-100' : 'opacity-0 scale-50'}`} />
      </div>
    </button>
  );
};

/**
 * Theme Toggle with Label
 */
export const ThemeToggleLabeled = ({ className = '' }) => {
  const { isDark, toggle } = useDarkMode();

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
        {isDark ? 'Dark' : 'Light'}
      </span>
      <ThemeToggleCompact />
    </div>
  );
};

export default ThemeToggle;
